import { SourceConfig, CandidatePayload } from '../types';
import { buildRelevanceFields } from '../relevance';
import {
  fetchHHSpecializations,
  compressCityIds as hhCompressCityIds,
  getAreaNameById as hhGetAreaNameById,
} from '../../services/hhDictionariesService';

/**
 * FR-3.1 + FR-3.3: Конфигурация и маппер для источника HH (HeadHunter).
 */
export const hhConfig: SourceConfig = {
  id: 'hh',
  label: 'HeadHunter',
  aiRecruiterSource: 'HH',
  enabledMessengers: ['HH', 'TELEGRAM', 'WHATSAPP', 'MAX'],
  messengerPriority: ['HH', 'TELEGRAM', 'WHATSAPP', 'MAX'],
  filterSections: ['includeNoAge', 'industry', 'specialization', 'positionExperience', 'experiencePeriod', 'jobSearchStatus', 'education', 'employmentType', 'citizenship', 'workFormat', 'metro', 'skills', 'companies', 'hasPhoto'],
  // Универсальные секции теперь читают данные/режим из sourceConfig, поэтому
  // конкретные fetch-функции HH явно прокидываются сюда — раньше они были
  // захардкожены внутри SpecializationModal.
  dataLoaders: {
    specialization: fetchHHSpecializations,
  },
  uiHints: {
    specializationMode: 'hierarchical',
    locationMode: 'hierarchical',
  },
  // hh-специфичный пост-процессинг выбранных city id'ов и имён:
  // - compressCityIds сворачивает все города региона в ID региона и
  //   делает слияние Москва+МО;
  // - getAreaNameById достаёт имя из закешированного словаря hh.
  // Для Avito/Estaff эти функции в их config'е не указаны (no-op).
  postProcessors: {
    compressCityIds: hhCompressCityIds,
    getAreaNameById: hhGetAreaNameById,
  },

  toPayload(candidate: Record<string, unknown>): CandidatePayload {
    const phone = normalizePhone(candidate.phone as string);
    const email = normalizeEmail(candidate.email);
    const telegram = normalizeString(candidate.telegram);
    const messengerLinks = isRecord(candidate.messengerLinks) ? candidate.messengerLinks : {};
    const whatsapp = normalizePhone(candidate.whatsapp as string)
      || normalizePhone(messengerLinks.whatsapp as string);
    const resumeUrl = normalizeString(candidate.resumeUrl);

    return {
      externalId: String(candidate.id),
      source: 'HH',
      sourceExternalId: String(candidate.id),
      email,
      messengerContacts: {
        telegram,
        whatsapp,
        phone,
        email,
        max: null,
        hhResumeId: String(candidate.id),
      },
      name: (candidate.fullName as string) || (candidate.name as string) || '',
      firstName: pickNamePart(candidate, 'firstName', 'first_name'),
      lastName: pickNamePart(candidate, 'lastName', 'last_name'),
      middleName: pickNamePart(candidate, 'middleName', 'middle_name'),
      hiddenName: isHiddenNameCandidate(candidate),
      photoUrl: (candidate.photo as string) || null,
      resumeUrl,
      resumeBrief: buildBrief(candidate),
      metadata: {
        experience: candidate.experience || '',
        skills: candidate.skills || [],
        resumeUrl,
        photoUrl: (candidate.photo as string) || null,
        lastUpdated: (candidate.updated as string) || new Date().toISOString(),
        source: 'hh',
      },
      ...buildRelevanceFields(candidate),
    };
  },
};

function normalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const compact = phone.trim().replace(/[\s()\-+]/g, '');
  if (/^\d+$/.test(compact)) {
    return compact.replace(/^8/, '7');
  }
  const digits = phone.replace(/[^0-9]/g, '');
  return digits ? digits.replace(/^8/, '7') : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function pickNamePart(candidate: Record<string, unknown>, camelName: string, snakeName: string): string | null {
  return normalizeString(candidate[camelName]) || normalizeString(candidate[snakeName]);
}

function isHiddenNameCandidate(candidate: Record<string, unknown>): boolean {
  const fullName = normalizeString(candidate.fullName);
  const displayName = normalizeString(candidate.name);
  const title = normalizeString(candidate.title);
  const titleOnlyName = Boolean(title)
    && (!displayName || displayName === title)
    && (!fullName || fullName === title || fullName === displayName);
  const hasNameParts = Boolean(
    pickNamePart(candidate, 'firstName', 'first_name')
    || pickNamePart(candidate, 'lastName', 'last_name')
    || pickNamePart(candidate, 'middleName', 'middle_name')
  );
  return !hasNameParts
    && titleOnlyName;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function normalizeEmail(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'object') {
    const candidate = value as Record<string, unknown>;
    const raw = candidate.email || candidate.value || candidate.formatted;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed || null;
    }
  }
  return null;
}

function buildBrief(candidate: Record<string, unknown>): string {
  const parts: string[] = [];
  if (candidate.experience) {
    const match = String(candidate.experience).match(/(\d+)/);
    if (match) {
      const months = parseInt(match[1]);
      const years = Math.floor(months / 12);
      parts.push(years > 0 ? `${years}+ лет опыта` : `${months} мес. опыта`);
    }
  }
  if (candidate.salary) {
    const cur = normalizeCurrencyCode(candidate.salaryCurrency) || 'RUR';
    parts.push(`Ожидаемая ЗП: ${candidate.salary} ${cur}`);
  }
  return parts.join(' | ') || (candidate.title as string) || '';
}

function normalizeCurrencyCode(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    return normalizeString(value);
  }
  if (typeof value === 'object') {
    const currency = value as Record<string, unknown>;
    return normalizeString(currency.code)
      || normalizeString(currency.value)
      || normalizeString(currency.formatted);
  }
  return null;
}

export default hhConfig;
